import { authedFetch } from "@/lib/authed-fetch";
import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import {
  Loader2,
  CheckCircle,
  XCircle,
  Cloud,
  ExternalLink,
  AlertTriangle,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  FolderOpen,
  Lock,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { DropboxBrowserDialog } from "./dropbox-browser-dialog";

interface ReadOnlyStatus {
  configured: boolean;
  authorized: boolean;
  connected: boolean;
  rootPath: string | null;
  rootExists?: boolean;
  accountEmail?: string;
  lastRefresh?: string;
  error?: string;
  needsReauthorization?: boolean;
  egress?: { usedBytes: number; budgetBytes: number };
}

function formatGB(bytes: number): string {
  return (bytes / 1024 ** 3).toFixed(bytes >= 1024 ** 3 ? 1 : 2) + " GB";
}

const STATUS_QUERY_KEY = ["/api/dropbox-readonly/status"];

export function DropboxReadOnlySettings() {
  const { toast } = useToast();

  const [appKey, setAppKey] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [showAppSecret, setShowAppSecret] = useState(false);
  const [authCode, setAuthCode] = useState("");
  const [setupOpen, setSetupOpen] = useState(false);
  const [browserOpen, setBrowserOpen] = useState(false);

  const { data: status, isLoading: statusLoading } = useQuery<ReadOnlyStatus>({
    queryKey: STATUS_QUERY_KEY,
  });

  // Open setup when unauthorized.
  useEffect(() => {
    if (status && !status.authorized) setSetupOpen(true);
  }, [status?.authorized]);

  const invalidateStatus = () => {
    queryClient.invalidateQueries({ queryKey: STATUS_QUERY_KEY });
  };

  const saveCredentialsMutation = useMutation({
    mutationFn: async () => {
      const res = await authedFetch("/api/dropbox-readonly/credentials", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ appKey: appKey.trim(), appSecret: appSecret.trim() }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || "Failed to save credentials");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Credentials saved",
        description: "The read-only app key and secret were saved.",
      });
      setAppSecret("");
      invalidateStatus();
    },
    onError: (error) => {
      toast({
        title: "Save failed",
        description: error instanceof Error ? error.message : "Could not save credentials",
        variant: "destructive",
      });
    },
  });

  const oauthUrlMutation = useMutation({
    mutationFn: async () => {
      const res = await authedFetch("/api/dropbox-readonly/oauth-url");
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || "Failed to generate authorization URL");
      }
      return res.json();
    },
    onSuccess: (data: { oauthUrl: string }) => {
      if (data.oauthUrl) {
        window.open(data.oauthUrl, "_blank", "noopener,noreferrer");
        toast({
          title: "Authorization opened",
          description: "Approve access in Dropbox, then paste the code below.",
        });
      }
    },
    onError: (error) => {
      toast({
        title: "Failed to open authorization",
        description: error instanceof Error ? error.message : "Could not generate authorization URL",
        variant: "destructive",
      });
    },
  });

  const exchangeCodeMutation = useMutation({
    mutationFn: async (code: string) => {
      const res = await authedFetch("/api/dropbox-readonly/exchange-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || "Code exchange failed");
      }
      return res.json();
    },
    onSuccess: (data: { success: boolean; connected: boolean; accountEmail?: string }) => {
      toast({
        title: "Connected!",
        description: data.accountEmail
          ? `Authorized as ${data.accountEmail}.`
          : "The read-only connection was authorized.",
      });
      setAuthCode("");
      invalidateStatus();
    },
    onError: (error) => {
      toast({
        title: "Code exchange failed",
        description: error instanceof Error ? error.message : "Could not exchange the authorization code",
        variant: "destructive",
      });
    },
  });

  const getStatusColor = (): "default" | "destructive" | "secondary" => {
    if (!status) return "secondary";
    if (status.error) return "destructive";
    if (status.connected) return "default";
    if (status.authorized) return "default";
    return "secondary";
  };

  const getStatusText = () => {
    if (statusLoading || !status) return "Loading…";
    if (status.error) return "Error";
    if (status.connected) return "Connected";
    if (status.authorized) return "Authorized";
    return "Not configured";
  };

  const getStatusIcon = () => {
    if (statusLoading) return <Loader2 className="h-4 w-4 animate-spin" />;
    if (!status) return <Cloud className="h-4 w-4" />;
    if (status.error) return <XCircle className="h-4 w-4" />;
    if (status.connected) return <CheckCircle className="h-4 w-4" />;
    if (status.authorized) return <CheckCircle className="h-4 w-4" />;
    return <Cloud className="h-4 w-4" />;
  };

  const canBrowse = !!(status?.connected && status?.rootPath);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">Read-Only Dropbox Browser</h3>
        <Badge
          variant={getStatusColor()}
          className="flex items-center gap-2"
          data-testid="badge-readonly-status"
        >
          {getStatusIcon()}
          {getStatusText()}
        </Badge>
      </div>

      {/* Info box */}
      <div className="text-sm text-gray-400 p-4 bg-gray-800/50 border border-gray-700 rounded-lg">
        <div className="flex items-center gap-2 font-medium text-gray-300 mb-2">
          <Lock className="h-4 w-4 text-blue-400" />
          Separate, read-only connection
        </div>
        <ul className="space-y-1 text-xs">
          <li>• This is a <strong>separate</strong> Dropbox app connection, independent of the primary storage connection.</li>
          <li>• It can only <strong>read and download</strong> files inside the configured folder — no writing, moving, or deleting.</li>
          <li>• Browsing is limited to the configured folder and its subfolders; nothing above it is accessible.</li>
          <li>• Your primary Dropbox connection is completely unaffected by these settings.</li>
        </ul>
      </div>

      {/* Status details */}
      {status && (status.accountEmail || status.error || status.rootPath) && (
        <div className="grid grid-cols-2 gap-4 p-4 navy-bg border border-gray-600 rounded-lg">
          {status.accountEmail && (
            <div>
              <div className="text-sm font-medium text-gray-300">Account</div>
              <div className="text-sm text-gray-400">{status.accountEmail}</div>
            </div>
          )}
          {status.rootPath && (
            <div>
              <div className="text-sm font-medium text-gray-300">Root Folder</div>
              <div className="text-sm text-gray-400 font-mono">{status.rootPath}</div>
            </div>
          )}
          {status.lastRefresh && (
            <div>
              <div className="text-sm font-medium text-gray-300">Last Refresh</div>
              <div className="text-sm text-gray-400">
                {new Date(status.lastRefresh).toLocaleString()}
              </div>
            </div>
          )}
          {status.egress && status.egress.usedBytes > 0 && (
            <div>
              <div className="text-sm font-medium text-gray-300">Downloads Today</div>
              <div className="text-sm text-gray-400">
                {formatGB(status.egress.usedBytes)} of {formatGB(status.egress.budgetBytes)} budget
              </div>
            </div>
          )}
          {status.error && (
            <div className="col-span-2">
              <div className="text-sm font-medium text-red-400">Error</div>
              <div className="text-sm text-red-300 font-mono bg-red-900/20 p-2 rounded border">
                {status.error}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Setup (collapsible) */}
      <Collapsible open={setupOpen} onOpenChange={setSetupOpen}>
        <CollapsibleTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-between border-gray-600 text-gray-300 hover:bg-gray-700"
            data-testid="button-toggle-readonly-setup"
          >
            <span className="font-medium">Setup &amp; Authorization</span>
            {setupOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-6 pt-4">
          {/* Step 1: credentials */}
          <div className="space-y-3">
            <div className="text-sm font-medium text-gray-300">
              Step 1: Read-only app key &amp; secret
            </div>
            <div className="space-y-2">
              <Label htmlFor="ro-app-key" className="text-gray-300 text-sm">
                App Key
              </Label>
              <Input
                id="ro-app-key"
                value={appKey}
                onChange={(e) => setAppKey(e.target.value)}
                placeholder="Enter the read-only app key…"
                className="bg-gray-800 border-gray-600 text-white font-mono text-sm"
                data-testid="input-readonly-app-key"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ro-app-secret" className="text-gray-300 text-sm">
                App Secret
              </Label>
              <div className="relative">
                <Input
                  id="ro-app-secret"
                  type={showAppSecret ? "text" : "password"}
                  value={appSecret}
                  onChange={(e) => setAppSecret(e.target.value)}
                  placeholder="Enter the read-only app secret…"
                  className="bg-gray-800 border-gray-600 text-white font-mono text-sm pr-10"
                  data-testid="input-readonly-app-secret"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 text-gray-400 hover:text-white"
                  onClick={() => setShowAppSecret(!showAppSecret)}
                >
                  {showAppSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => saveCredentialsMutation.mutate()}
                disabled={!appKey.trim() || !appSecret.trim() || saveCredentialsMutation.isPending}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="button-save-readonly-credentials"
              >
                {saveCredentialsMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Save Credentials
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Step 2: authorize */}
          <div className="space-y-3">
            <div className="text-sm font-medium text-gray-300">Step 2: Authorize with Dropbox</div>
            <Button
              onClick={() => oauthUrlMutation.mutate()}
              disabled={!status?.configured || oauthUrlMutation.isPending}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="button-open-readonly-oauth"
            >
              {oauthUrlMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Opening…
                </>
              ) : (
                <>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open Dropbox Authorization
                </>
              )}
            </Button>
            {!status?.configured && (
              <div className="text-xs text-gray-500">
                Save the app key and secret first to enable authorization.
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="ro-auth-code" className="text-gray-300 text-sm">
                Authorization Code
              </Label>
              <div className="flex gap-2">
                <Input
                  id="ro-auth-code"
                  value={authCode}
                  onChange={(e) => setAuthCode(e.target.value)}
                  placeholder="Paste the authorization code here…"
                  className="flex-1 bg-gray-800 border-gray-600 text-white font-mono text-sm"
                  data-testid="input-readonly-auth-code"
                />
                <Button
                  onClick={() => authCode.trim() && exchangeCodeMutation.mutate(authCode.trim())}
                  disabled={!authCode.trim() || exchangeCodeMutation.isPending}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="button-exchange-readonly-code"
                >
                  {exchangeCodeMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Exchange"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Root folder (locked) */}
      <div className="space-y-3 p-4 navy-bg border border-gray-600 rounded-lg">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-300">
          <Lock className="h-4 w-4 text-blue-400" />
          Allowed Folder (locked)
        </div>
        <div
          className="text-sm text-white font-mono bg-gray-800 border border-gray-600 rounded px-3 py-2"
          data-testid="text-readonly-root-path"
        >
          {status?.rootPath || "—"}
        </div>
        <div className="text-xs text-gray-400">
          For security, this folder is fixed in the application itself and cannot be changed
          here — not even by administrators. Browsing and downloads are permanently limited to
          this folder and its subfolders.
        </div>
        {status?.rootPath && status?.rootExists === false && (
          <Alert className="bg-yellow-900/20 border-yellow-600">
            <AlertTriangle className="h-4 w-4 text-yellow-400" />
            <AlertDescription className="text-yellow-300">
              The configured folder <span className="font-mono">{status.rootPath}</span> does not
              exist in the connected Dropbox account.
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Browse */}
      {canBrowse && (
        <div className="flex justify-start">
          <Button
            onClick={() => setBrowserOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="button-open-readonly-browser"
          >
            <FolderOpen className="h-4 w-4 mr-2" />
            Browse files
          </Button>
        </div>
      )}

      <DropboxBrowserDialog open={browserOpen} onOpenChange={setBrowserOpen} />
    </div>
  );
}
